/**
 * Scheduled messages in the conversations and the Scheduled list (core ScheduledViewModel, logic/scheduled): the
 * composer's send menu ("Tomorrow at 9:00 AM", "Monday at 9:00 AM", "Custom time"), the bar above the composer
 * ("2 scheduled messages · See all") and the list with edit / reschedule / send now / cancel.
 */
import type { ChannelViewModel, ScheduledRow, ScheduledViewModel } from "@bundle/core";
import { Scheduled } from "@bundle/core";
import { useState, useSyncExternalStore, type ReactNode } from "react";
import { ScheduleSheet } from "../components/RemindSheet.tsx";
import { Button, Icon, List, Stack, Text, TextField, type MenuEntry } from "../primitives/index.ts";
import { ConversationRow, ListState } from "./Drafts.tsx";
import { useFormat, useT } from "../i18n/I18nProvider.tsx";
import { useViewModel, useWorkspace } from "../hooks/index.ts";
import { useScheduled } from "./registry.ts";

const noSubscribe = () => () => {};

/** The composer's send menu and its "Custom time" sheet, for this conversation's ChannelViewModel. */
export function useScheduleMenu(vm: ChannelViewModel | undefined, testID: string): { sendMenu?: { label: string; items: MenuEntry[] }; sheet: ReactNode } {
  const t = useT();
  const fmt = useFormat();
  const ws = useWorkspace();
  const scheduled = useScheduled();
  const [custom, setCustom] = useState(false);
  // Re-render when the draft changes (canSchedule).
  useSyncExternalStore(vm?.subscribe ?? noSubscribe, () => vm?.state.draft);
  if (vm === undefined || scheduled === undefined) return { sheet: null };
  const now = ws.vm.deps.clock.now();
  const offset = ws.vm.deps.clock.tzOffsetMinutes(now);
  const can = vm.canSchedule;
  const items: MenuEntry[] = Scheduled.schedulePresets(now, offset).map((p) => ({
    id: `schedule-${p.id}`,
    label: t(p.id === "tomorrow" ? "schedule.tomorrow" : "schedule.monday", { time: fmt.time(p.at) }),
    icon: "clock",
    disabled: !can,
    onSelect: () => vm.scheduleDraft(p.at),
  }));
  items.push({ id: "schedule-custom", label: t("schedule.custom"), icon: "calendar", disabled: !can, onSelect: () => setCustom(true) });
  return {
    sendMenu: { label: t("schedule.menu"), items },
    sheet: (
      <ScheduleSheet
        open={custom}
        onClose={() => setCustom(false)}
        onPick={(at) => vm.scheduleDraft(at)}
        now={now}
        offsetMinutes={offset}
        title={t("schedule.title")}
        setLabel={t("schedule.set")}
        testID={`${testID}-schedule-sheet`}
      />
    ),
  };
}

/** "2 scheduled messages · See all" above the composer (nothing when there are none). */
export function ScheduledBar({ channelId, threadRootId, onSeeAll, testID }: { channelId: string; threadRootId?: string; onSeeAll: () => void; testID: string }) {
  const t = useT();
  const scheduled = useScheduled();
  useSyncExternalStore(scheduled?.subscribe ?? noSubscribe, () => scheduled?.state.messages);
  const count = scheduled?.forConversation(channelId, threadRootId).length ?? 0;
  if (count === 0) return null;
  return (
    <Stack direction="horizontal" align="center" gap="2" paddingX="2" testID={testID}>
      <Icon name="send" size="icon.sm" color="text.secondary" />
      <Text variant="footnote" color="text.secondary">
        {t("scheduled.bar", { count })}
      </Text>
      <Button variant="ghost" size="sm" label={t("scheduled.seeAll")} onPress={onSeeAll} testID={`${testID}-see-all`} />
    </Stack>
  );
}

/** The Scheduled list: all my scheduled messages, soonest first, in the Drafts & sent row style. */
export function ScheduledList({ vm, onOpen, testID }: { vm: ScheduledViewModel; onOpen: (row: ScheduledRow) => void; testID: string }) {
  const t = useT();
  const s = useViewModel(vm);
  if (s.status === "loading") return <ListState testID={`${testID}-loading`} />;
  if (s.status === "failed") return <ListState icon="offline" body={t("scheduled.failed")} action={{ label: t("later.retry"), onPress: () => void vm.refresh() }} testID={`${testID}-failed`} />;
  const rows = vm.rows();
  if (rows.length === 0) return <ListState icon="clock" title={t("drafts.scheduledEmpty.title")} body={t("drafts.scheduledEmpty.body")} testID={`${testID}-empty`} />;
  return (
    <List
      items={rows}
      keyOf={(row) => row.id}
      header={<div style={{ height: 8 }} />}
      testID={`${testID}-list`}
      renderItem={(row) => <ScheduledRowView vm={vm} row={row} editing={s.editing?.id === row.id ? s.editing : undefined} onOpen={() => onOpen(row)} testID={`${testID}-row-${row.id}`} />}
    />
  );
}

function ScheduledRowView({ vm, row, editing, onOpen, testID }: { vm: ScheduledViewModel; row: ScheduledRow; editing?: { text: string; sendAt: number }; onOpen: () => void; testID: string }) {
  const t = useT();
  const fmt = useFormat();
  const ws = useWorkspace();
  const [rescheduling, setRescheduling] = useState(false);
  const s = row.scheduled;
  const now = ws.vm.deps.clock.now();
  const title = row.inThread ? t("drafts.inThread", { conversation: row.conversation }) : row.conversation;
  return (
    <>
      <ConversationRow
        channelId={s.channelId}
        title={title}
        onPress={editing === undefined ? onOpen : undefined}
        hoverActions={[
          { id: "send-now", label: t("scheduled.sendNow"), icon: "send", onSelect: () => void vm.sendNow(s.id) },
          { id: "edit", label: t("scheduled.edit"), icon: "edit", onSelect: () => vm.startEdit(s.id) },
          { id: "reschedule", label: t("scheduled.reschedule"), icon: "clock", onSelect: () => setRescheduling(true) },
        ]}
        moreActions={{ label: t("scheduled.more"), items: [{ id: "cancel", label: t("scheduled.cancel"), icon: "trash", destructive: true, onSelect: () => vm.cancel(s.id) }] }}
        accessibilityLabel={`${title}: ${s.text}`}
        testID={testID}
      >
        <Stack direction="horizontal" align="center" gap="1">
          <Icon name="clock" size="icon.sm" color={s.failed === undefined ? "accent.text" : "status.danger.fg"} />
          <Text variant="footnote" weight="semibold" color={s.failed === undefined ? "accent.text" : "status.danger.fg"}>
            {t("scheduled.sendsAt", { when: fmt.timestamp(s.sendAt) })}
          </Text>
        </Stack>
        {editing === undefined ? (
          s.text === "" ? null : <Text lines={2}>{s.text}</Text>
        ) : (
          <Stack gap="2" paddingY="1">
            <TextField value={editing.text} onChange={(v) => vm.setEditText(v)} multiline autoFocus accessibilityLabel={t("scheduled.edit")} testID={`${testID}-edit`} />
            <Stack direction="horizontal" gap="2" justify="end">
              <Button variant="ghost" size="sm" label={t("common.cancel")} onPress={() => vm.cancelEdit()} testID={`${testID}-edit-cancel`} />
              <Button variant="primary" size="sm" label={t("scheduled.save")} disabled={editing.text.trim() === ""} onPress={() => vm.saveEdit()} testID={`${testID}-edit-save`} />
            </Stack>
          </Stack>
        )}
        {s.files === undefined || s.files.length === 0 ? null : (
          <Stack direction="horizontal" align="center" gap="1" testID={`${testID}-files`}>
            <Icon name="attach" size="icon.sm" color="text.tertiary" />
            <Text variant="footnote" color="text.secondary" lines={1}>
              {s.files.length === 1 ? s.files[0]!.name : t("scheduled.files", { count: s.files.length })}
            </Text>
          </Stack>
        )}
        {s.failed === undefined ? null : (
          <Text variant="footnote" color="status.danger.fg" testID={`${testID}-failed`}>
            {t("scheduled.notSent")}
          </Text>
        )}
      </ConversationRow>
      <ScheduleSheet
        open={rescheduling}
        onClose={() => setRescheduling(false)}
        onPick={(at) => vm.reschedule(s.id, at)}
        now={now}
        offsetMinutes={ws.vm.deps.clock.tzOffsetMinutes(now)}
        title={t("scheduled.reschedule")}
        setLabel={t("scheduled.reschedule")}
        testID={`${testID}-reschedule`}
      />
    </>
  );
}
